import * as THREE from "three";
import type { MapLook } from "../maps";
import { toon } from "./toon";

// Bomb the Hill's road kit (#217, out of `hill-course.ts`): one set of
// materials per build of the road, shared by every chunk, obstacle and
// car so the batches stay a few meshes.

export const PAINTS = ["#e63946", "#457b9d", "#f4a261", "#2a9d8f", "#f1faee", "#6d597a", "#ffb703", "#8d99ae"];

/** One set of materials per build, shared by every obstacle so the batch stays a few meshes. */
export interface Kit {
  paints: THREE.Material[];
  glass: THREE.Material;
  tyre: THREE.Material;
  trim: THREE.Material;
  cone: THREE.Material;
  white: THREE.Material;
  bin: THREE.Material;
  lid: THREE.Material;
  stripe: THREE.Material;
  metal: THREE.Material;
  hole: THREE.Material;
  rim: THREE.Material;
  gravel: THREE.Material;
  pebble: THREE.Material;
  paint: THREE.Material;
  asphalt: THREE.Material;
  pole: THREE.Material;
  lampOff: THREE.Material;
  red: THREE.Material;
  amber: THREE.Material;
  green: THREE.Material;
  headlight: THREE.Material;
  beam: THREE.Material;
  wall: THREE.Material[];
  /** A crosswalk's pavements and footpaths (#202). */
  pavement: THREE.Material;
  /** The shopfront behind a crosswalk (#202): its walls, its awnings, its lit doors, and its walk lights lit. */
  shop: THREE.Material;
  awning: THREE.Material;
  door: THREE.Material;
  walk: THREE.Material;
  /** The roadside's (#211) materials, by the scenery kit's keys: one set for every chunk (#216). */
  scenery: Map<string, THREE.Material>;
}

/** Drawn just over the road whatever the depth buffer's precision far away, so a patch on it never flickers into it (#209). */
export const OVER_ROAD = { polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 } as const;

export function hillKit(look: MapLook): Kit {
  return {
    asphalt: toon(look.floor, { roughness: 0.95 }, "concrete"),
    pole: toon("#3d4450", { roughness: 0.5, metalness: 0.4 }, "metal"),
    lampOff: toon("#2a2d33", { roughness: 0.6 }, "plastic"),
    red: new THREE.MeshBasicMaterial({ color: "#ff3b30" }),
    amber: new THREE.MeshBasicMaterial({ color: "#ffb020" }),
    green: new THREE.MeshBasicMaterial({ color: "#3dff7a" }),
    headlight: new THREE.MeshBasicMaterial({ color: "#fff3c4" }),
    beam: new THREE.MeshBasicMaterial({ color: "#fff3c4", transparent: true, opacity: 0.22, depthWrite: false }),
    wall: [toon(look.walls[1] ?? "#d8d6cf", { roughness: 0.75 }, "paint"), toon(look.walls[3] ?? "#d8d6cf", { roughness: 0.75 }, "paint")],
    paints: PAINTS.map((c) => toon(c, { roughness: 0.4 }, "paint")),
    glass: toon("#2b3a4a", { roughness: 0.15, metalness: 0.2 }, "glass"),
    tyre: toon("#22232b", { roughness: 0.85 }, "plastic"),
    trim: toon("#c9ced6", { roughness: 0.35, metalness: 0.5 }, "metal"),
    cone: toon("#ff6b1a", { roughness: 0.5 }, "plastic"),
    white: toon("#f6f4ee", { roughness: 0.5 }, "plastic"),
    bin: toon("#2f7d4f", { roughness: 0.6 }, "plastic"),
    lid: toon("#245f3c", { roughness: 0.6 }, "plastic"),
    stripe: toon("#d62828", { roughness: 0.5 }, "paintedMetal"),
    metal: toon("#9aa3ad", { roughness: 0.35, metalness: 0.5 }, "metal"),
    // A pothole reads at speed in every style (#209): a hole that's near black in any light
    // (unlit, so Clay's pastels can't lift it), a pale cracked rim, and a pale gravel patch strewn with dark stones.
    hole: new THREE.MeshBasicMaterial({ color: "#0b0b0d", ...OVER_ROAD, polygonOffsetFactor: -4, polygonOffsetUnits: -4 }),
    rim: toon("#ddd6c4", { roughness: 1, ...OVER_ROAD }, "concrete"),
    gravel: toon("#dcc79b", { roughness: 1, ...OVER_ROAD }, "concrete"),
    pebble: toon("#463d32", { roughness: 1 }, "concrete"),
    paint: toon("#f2efe2", { roughness: 0.9 }, "concrete"),
    pavement: toon("#c4beb1", { roughness: 0.95 }, "concrete"),
    shop: toon("#d7b98f", { roughness: 0.8 }, "paint"),
    awning: toon("#2f8f83", { roughness: 0.7 }, "paint"),
    door: new THREE.MeshBasicMaterial({ color: "#ffd98a" }),
    walk: new THREE.MeshBasicMaterial({ color: "#eafff6" }),
    scenery: new Map(),
  };
}

/** Every material a kit holds: shared by every chunk and car, freed only with the road. */
export const kitMaterials = (k: Kit): THREE.Material[] => [...k.paints, ...k.wall, ...k.scenery.values(), ...Object.values(k).filter((m): m is THREE.Material => m instanceof THREE.Material)];

export const paintOf = (k: Kit, tint: number) => k.paints[Math.floor(tint * k.paints.length) % k.paints.length]!;
